import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { mockExam2Questions } from './data/mockExam2Questions';
import Exam2QuestionCard from './components/Exam2QuestionCard';
import Exam2Navigator from './components/Exam2Navigator';
import Exam2Results from './components/Exam2Results';
import ExamTimer from '../mock-exam/components/ExamTimer';

const TOTAL_SECONDS = 70 * 60;
type ExamPhase = 'intro' | 'exam' | 'results' | 'review';

export default function MockExam2Page() {
  const [phase, setPhase] = useState<ExamPhase>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<(string | null)[]>(
    new Array(mockExam2Questions.length).fill(null)
  );
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [timeUsed, setTimeUsed] = useState(0);
  const [flagged, setFlagged] = useState<boolean[]>(new Array(mockExam2Questions.length).fill(false));
  const [showSolution, setShowSolution] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const handleTimeUp = useCallback(() => {
    stopTimer(); setTimeUsed(TOTAL_SECONDS); setPhase('results');
  }, [stopTimer]);

  useEffect(() => {
    if (phase === 'exam') {
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => { if (prev <= 1) { handleTimeUp(); return 0; } return prev - 1; });
      }, 1000);
    }
    return () => stopTimer();
  }, [phase, handleTimeUp, stopTimer]);

  const handleStart = () => {
    setPhase('exam'); setCurrentIndex(0);
    setUserAnswers(new Array(mockExam2Questions.length).fill(null));
    setSecondsLeft(TOTAL_SECONDS);
    setFlagged(new Array(mockExam2Questions.length).fill(false));
    setShowSolution(false);
  };

  const handleAnswerSelect = (answer: string) => {
    const updated = [...userAnswers]; updated[currentIndex] = answer; setUserAnswers(updated); setShowSolution(true);
  };

  const handleNext = () => { setShowSolution(false); if (currentIndex < mockExam2Questions.length - 1) setCurrentIndex(currentIndex + 1); };
  const handlePrev = () => { setShowSolution(false); if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const handleJump = (index: number) => { setShowSolution(false); setCurrentIndex(index); };
  const handleToggleFlag = (index: number) => { const updated = [...flagged]; updated[index] = !updated[index]; setFlagged(updated); };

  const handleSubmit = () => {
    const used = TOTAL_SECONDS - secondsLeft; setTimeUsed(used); stopTimer(); setShowSubmitModal(false); setPhase('results');
  };

  const handleRetake = () => {
    setPhase('intro'); setCurrentIndex(0);
    setUserAnswers(new Array(mockExam2Questions.length).fill(null));
    setSecondsLeft(TOTAL_SECONDS); setTimeUsed(0);
    setFlagged(new Array(mockExam2Questions.length).fill(false)); setShowSolution(false);
  };

  const handleReview = () => { setPhase('review'); setCurrentIndex(0); setShowSolution(true); };

  const answeredCount = userAnswers.filter((a) => a !== null).length;
  const unansweredCount = mockExam2Questions.length - answeredCount;
  const currentQuestion = mockExam2Questions[currentIndex];

  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-rose-50">
        <div className="bg-gradient-to-r from-violet-600 to-rose-500 text-white py-14">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-6">
              <i className="ri-timer-line text-4xl" />
            </div>
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <i className="ri-medal-line" /> Mock Exam 2 of 3
            </div>
            <h1 className="text-5xl font-black mb-4">SAT Math Mock Exam 2</h1>
            <p className="text-xl text-violet-100 max-w-2xl mx-auto">
              44 questions · 70 minutes · All 4 domains · Brand-new question set
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: 'ri-question-line', label: 'Questions', value: '44', color: 'text-violet-600' },
              { icon: 'ri-time-line', label: 'Time Limit', value: '70 min', color: 'text-rose-600' },
              { icon: 'ri-layout-grid-line', label: 'Domains', value: '4', color: 'text-orange-600' },
              { icon: 'ri-calculator-line', label: 'Calculator', value: 'Allowed', color: 'text-sky-600' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl shadow-md p-5 text-center">
                <div className={`w-10 h-10 flex items-center justify-center mx-auto mb-2 ${s.color}`}>
                  <i className={`${s.icon} text-2xl`} />
                </div>
                <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 font-medium mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">What's Covered</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { domain: 'Algebra', count: 11, topics: 'Linear equations, inequalities, systems, functions', color: 'from-violet-500 to-violet-600', icon: 'ri-function-line' },
                { domain: 'Advanced Math', count: 11, topics: 'Quadratics, polynomials, exponentials, radicals, functions', color: 'from-rose-500 to-rose-600', icon: 'ri-superscript-2' },
                { domain: 'Problem-Solving & Data Analysis', count: 11, topics: 'Ratios, percents, statistics, probability, data interpretation', color: 'from-orange-500 to-orange-600', icon: 'ri-bar-chart-line' },
                { domain: 'Geometry & Trigonometry', count: 11, topics: 'Area, volume, Pythagorean theorem, trig, coordinate geometry', color: 'from-sky-500 to-sky-600', icon: 'ri-shape-line' },
              ].map((d) => (
                <div key={d.domain} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${d.color} flex items-center justify-center text-white flex-shrink-0`}>
                    <i className={`${d.icon} text-xl`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">{d.domain}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">{d.count} Qs</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{d.topics}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-violet-50 border border-violet-200 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-violet-800 mb-3 flex items-center gap-2">
              <i className="ri-information-line text-violet-600 w-5 h-5 flex items-center justify-center" />
              Before You Begin
            </h3>
            <ul className="space-y-2 text-sm text-violet-700">
              {[
                'The timer starts immediately when you click "Start Exam" — 70 minutes on the clock.',
                'Navigate freely between questions using the Question Navigator panel.',
                'Flag questions you want to revisit — they\'ll be highlighted in the navigator.',
                'Step-by-step solutions appear immediately after you select an answer.',
                'The exam auto-submits when time runs out.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-4">
            <Link to="/sat-prep" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-2" />Back
            </Link>
            <button onClick={handleStart} className="flex-1 py-4 bg-gradient-to-r from-violet-500 to-rose-500 text-white font-black text-lg rounded-xl hover:from-violet-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-3">
              <i className="ri-play-circle-line text-2xl" />
              Start Exam — 70 Minutes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-rose-50">
        <Exam2Results questions={mockExam2Questions} userAnswers={userAnswers} timeUsed={timeUsed} totalTime={TOTAL_SECONDS} onReview={handleReview} onRetake={handleRetake} />
      </div>
    );
  }

  const isReview = phase === 'review';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-rose-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {isReview ? (
              <button onClick={() => setPhase('results')} className="flex items-center gap-2 text-violet-600 hover:text-violet-700 font-semibold text-sm whitespace-nowrap">
                <i className="ri-arrow-left-line" />Back to Results
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                  <i className="ri-timer-line text-violet-600" />
                </div>
                <span className="font-bold text-gray-900 text-sm hidden sm:block">SAT Math Mock Exam 2</span>
              </div>
            )}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{answeredCount}/44</span>
              <span>answered</span>
              {flagged.filter(Boolean).length > 0 && (
                <span className="text-amber-600 font-medium">· {flagged.filter(Boolean).length} flagged</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isReview && <span className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-bold">Review Mode</span>}
            {!isReview && <ExamTimer secondsLeft={secondsLeft} totalSeconds={TOTAL_SECONDS} />}
            {!isReview && (
              <button onClick={() => setShowSubmitModal(true)} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-lg text-sm transition-colors whitespace-nowrap">
                Submit Exam
              </button>
            )}
          </div>
        </div>
        {!isReview && (
          <div className="h-1 bg-gray-100">
            <div className="h-full bg-gradient-to-r from-violet-500 to-rose-500 transition-all duration-500" style={{ width: `${(answeredCount / mockExam2Questions.length) * 100}%` }} />
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <Exam2QuestionCard
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              totalQuestions={mockExam2Questions.length}
              selectedAnswer={userAnswers[currentIndex]}
              onAnswerSelect={handleAnswerSelect}
              showSolution={isReview || showSolution}
            />
            <div className="flex items-center justify-between mt-6">
              <button onClick={handlePrev} disabled={currentIndex === 0} className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap">
                <i className="ri-arrow-left-line" />Previous
              </button>
              <button onClick={() => handleToggleFlag(currentIndex)} className={`px-4 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors whitespace-nowrap ${flagged[currentIndex] ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-white border-2 border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <i className={`ri-flag-${flagged[currentIndex] ? 'fill' : 'line'}`} />
                {flagged[currentIndex] ? 'Flagged' : 'Flag'}
              </button>
              <button onClick={handleNext} disabled={currentIndex === mockExam2Questions.length - 1} className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap">
                Next<i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <Exam2Navigator questions={mockExam2Questions} currentIndex={currentIndex} userAnswers={userAnswers} onJump={handleJump} flagged={flagged} onToggleFlag={handleToggleFlag} />
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-send-plane-line text-violet-600 text-3xl" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Submit Exam?</h2>
              <p className="text-gray-600">
                You have answered <strong>{answeredCount}</strong> of <strong>44</strong> questions.
                {unansweredCount > 0 && <span className="text-amber-600 font-semibold"> {unansweredCount} question{unansweredCount > 1 ? 's' : ''} left unanswered.</span>}
              </p>
            </div>
            {unansweredCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-700">
                <i className="ri-alert-line mr-2" />Unanswered questions will be marked incorrect.
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowSubmitModal(false)} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors whitespace-nowrap">Keep Going</button>
              <button onClick={handleSubmit} className="flex-1 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-colors whitespace-nowrap">Submit Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
